import type { EmailCheck } from './email-check';
import type { IdentityAskState } from './identity-ask';
/**
 * AgentUI — the one entry point that picks which agent surface renders.
 *
 *   demoMode     → DemoView       (server-driven browser, streamed as video)
 *   websiteAgent → WebsiteAgentView (top-of-funnel Q&A)
 *   neither      → OnboardingView   (drives the HOST page)
 *
 * FLO-51: the onboarding branch used to render its own bespoke panel
 * (TranscriptSidebar). It now renders the same chat surface as the website
 * agent — one set of controls, one set of button positions — docked rather than
 * modal, because it has to leave the customer's product visible and clickable.
 */

import React, { useEffect } from 'react';
import {
  resolveAvatarIdleAfterMs,
  useAvatarIdle,
  useAvatarSurfaceActive,
} from './avatar-idle';
import { OnboardingView } from '../components/OnboardingView';
import { DemoView } from '../components/DemoView';
import {
  WebsiteAgentView,
  WebsiteAgentNudge,
  WebsiteAgentCaptureConfig,
  NudgeTelemetryEvent,
} from '../components/WebsiteAgentView';
import type { WebsiteAgentDisplayMode } from '../components/websiteAgentLayout';
import type { DemoIntroPrefill, DemoCapturePolicy } from '../components/demoLayout';
import type { PersistedMessage } from './wa-continuity';
import {
  AgentState,
  ConnectionProgress,
  TranscriptMessage,
  type WebsiteAgentStartOptions,
} from '../types/agent';
import { logger } from './logger';
import { getString } from './ui-strings';
import type { SupportedLanguage } from './ui-strings';
import type { LanguageOption, PendingLanguageSwitch } from './language-preference';
import type { BookingUxHint } from './booking-ux-hint';
import type { BookingIntentResolver } from './booking-attribution';

export interface PlanProgress {
  planName: string;
  currentStep: number;
  totalSteps: number;
  progressPercent: number;
  currentTaskTitle: string;
}

interface AgentUIProps {
  connected: boolean;
  voiceEnabled: boolean;
  agentState: AgentState;
  botTranscript?: string;
  userTranscript?: string;
  screenShareEnabled?: boolean;
  userIsSpeaking?: boolean;
  botIsSpeaking?: boolean;
  audioLevels?: number[];
  onToggleConnection?: () => void;
  onToggleMute?: () => void;
  /** FLO-51: browser refused the mic — surfaced on the mic control. */
  micPermissionDenied?: boolean;
  /** A mic request failed for a non-permission reason (missing/busy device). */
  micUnavailable?: boolean;
  onToggleScreenShare?: () => void;
  transcriptHistory?: TranscriptMessage[];
  onResume?: () => void;
  pausedSessionId?: string;
  connectionProgress?: ConnectionProgress;
  fatalError?: string;
  isReturningUser?: boolean;
  /**
   * FLO-534: the site runs the onboarding agent in support mode (on-demand).
   * Since FLO-51 every panel starts collapsed, so this no longer decides THAT;
   * it decides what the agent opens WITH (a support question, not a setup
   * pitch) and — via the SDK's resolveNudgeAutoShow — that it does not nudge.
   */
  supportMode?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
  /**
   * FLO-51: end the onboarding session (tears down pipecat). Distinct from
   * `onMinimize`, which only collapses the panel and leaves the paid session
   * running — the two used to be near-identical header buttons.
   */
  onEndSession?: () => void;
  /** FLO-531: `messageKind` marks a message the server must recognise by role
   *  rather than wording (the demo kickoff), so a translated form still fires it. */
  onSendText?: (text: string, options?: { messageKind?: 'intro_form' }) => void;
  // FLO-307: deterministic opener chips (demo mode) + tap handler. Rendered by
  // DemoView above the text input; server-pushed at kickoff, cleared on the
  // next turn by the SDK.
  suggestedOptions?: string[];
  onSelectOption?: (text: string) => void;
  // Nudge props
  nudgeText?: string;
  showNudge?: boolean;
  onNudgeDismiss?: () => void;
  nudgeAutoHideDelay?: number;
  nudgeAutoShow?: boolean;
  // Plan progress props
  planProgress?: PlanProgress;
  languageOptions?: LanguageOption[];
  selectedLanguage?: SupportedLanguage;
  pendingLanguageSwitch?: PendingLanguageSwitch | null;
  languageSwitchError?: string | null;
  languageSwitchDisabled?: boolean;
  onLanguageChange?: (language: SupportedLanguage) => void;
  // Demo mode props
  demoMode?: boolean;
  // FLO-322: server truth — a live demo is entitled AND runnable. Gates the
  // push-to-demo "see it live" CTA (introduced by the website-agent UX, FLO-349)
  // so it's never a dead CTA. Fail-safe false when the API doesn't supply it.
  demoAvailable?: boolean;
  /** Entitlement to start a website-agent session at all (validate-client-key). */
  websiteAgentEnabled?: boolean;
  // FLO-320: website-agent mode + explicit-intent session starter.
  websiteAgent?: boolean;
  onStartWebsiteAgent?: (options?: WebsiteAgentStartOptions) => void | Promise<void>;
  /**
   * FLO-786: request the microphone once the agent's spoken welcome ends, for a
   * session the visitor opened with a voice launcher. Resolves false when the
   * browser took no decision, which is the surface's cue to ask with a tap.
   */
  onRequestMicAfterWelcome?: () => Promise<boolean>;
  onOpenWebsiteAgentPreview?: () => void;
  /** Hidden control-plane requests; neither creates a visible visitor turn. */
  onRequestWebsiteAgentDemo?: () => void;
  onRequestWebsiteAgentIdentityPrompt?: () => void;
  // FLO-318/349: resolved website-agent config → opener chips, section-aware
  // floating nudge pills, optional avatar, and the push-to-demo CTA.
  websiteAgentOpenerChips?: string[];
  websiteAgentNudges?: WebsiteAgentNudge[];
  websiteAgentAvatarEnabled?: boolean;
  // FLO-520/FLO-753: enabled avatar media selects the video-tile launcher and
  // panel hero; disabled or absent media falls back to the pill. Forwarded raw —
  // WebsiteAgentView applies the enabled gate and URL whitelist.
  websiteAgentAvatarIdleLoopUrl?: string;
  websiteAgentAvatarPosterUrl?: string;
  /** The raw approach-intro facet; validated in `resolveAvatarMedia`. */
  websiteAgentAvatarIntro?: unknown;
  websiteAgentPushToDemo?: boolean;
  websiteAgentCtaLabel?: string;
  /**
   * FLO-618: the bot asked the website agent to show the booking calendar
   * (`show_calendar`). A monotonic counter, not a flag — see the prop's doc in
   * WebsiteAgentView. The calendar URL itself is `demoCalendarLink` below: one
   * resolution (`getResolvedCalendarUrl`) feeds both agents, so the demo's
   * post-demo embed and the website agent's panel can never point at different
   * calendars for the same site.
   */
  websiteAgentCalendarRequestId?: number;
  /**
   * The website→demo hand-over's two server edges, as monotonic counters:
   * `demo_starting` (the branded stage card goes up) and `demo_visible` (the
   * picture is worth revealing). Forwarded raw — WebsiteAgentView's reducer owns
   * the three reveal edges and the reset. Both 0 on an older pipecat, which is
   * the pre-card behaviour, and both 0 on every non-website surface: the SDK's
   * branches for these messages are gated on `config.websiteAgent`.
   */
  websiteAgentDemoStartingId?: number;
  websiteAgentDemoVisibleId?: number;
  // FLO-514/519: floating card (default) vs docked right rail + its width.
  // Forwarded raw — WebsiteAgentView resolves unknown/absent values to
  // 'floating' / 400px.
  websiteAgentDisplayMode?: WebsiteAgentDisplayMode;
  websiteAgentSidebarWidth?: number;
  // FLO-544: turns from a conversation the server RESUMED. Replaces the
  // sessionStorage-restored history in the rail (sidebar mode only, and only
  // when the server actually had turns — see reconcileRestoredMessages).
  websiteAgentResumedMessages?: PersistedMessage[];
  // Resume honesty: how this session's open/resume ended. 'fresh' over a
  // restored transcript renders the "starting fresh" divider — the bot has no
  // memory of what is painted. Undefined = no session yet (dormant restore).
  websiteAgentResumeOutcome?: 'resumed' | 'fresh';
  // FLO-325: config-gated identity capture + its submit handler.
  websiteAgentCapture?: WebsiteAgentCaptureConfig;
  // FLO-367: nudge telemetry sink (SDK batches + beacon-flushes).
  onWebsiteAgentNudgeEvent?: (e: NudgeTelemetryEvent) => void;
  // The visitor moved to another page without a reload (website-agent-nudges.ts).
  onWebsiteAgentNavigated?: () => void;
  // CDP Phase 1 (FLO-547): real open/close transitions of the website-agent
  // panel (the FLO-524 visual restore is NOT an open) + the booking-CTA click
  // proxy (DemoView's Cal/Calendly CTAs).
  onWidgetOpened?: (trigger: 'user' | 'nudge' | 'auto') => void;
  onWidgetClosed?: () => void;
  onBookingCtaClick?: BookingIntentResolver;
  /** Bounded booking URL resolution for automatic calendar transitions. */
  onResolveBookingIntent?: BookingIntentResolver;
  /** Provider iframe hint for immediate UX only; webhooks own conversion. */
  onBookingUxHint?: (hint: BookingUxHint) => void;
  // FLO-320: end the website-agent session (tears down pipecat). Wired to the
  // header "End" button in WebsiteAgentView.
  onEndWebsiteAgent?: () => void;
  /** FLO-285: 'docked' (bottom pill → full-bleed on start) | 'fullscreen'. */
  embedMode?: 'docked' | 'fullscreen';
  demoIntroFormSubmitted?: boolean;
  onMarkIntroFormSubmitted?: () => void;
  botVideoStream?: MediaStream | null;
  avatarVideoStream?: MediaStream | null;
  /**
   * Silence, in ms, after which the live avatar track gives way to the idle
   * loop. `0` disables. See `avatar-idle.ts` — this is a rendering choice, not
   * a billing one; it does not reduce what the avatar costs.
   */
  avatarIdleAfterMs?: number;
  onEndDemo?: () => void;
  onCloseDemo?: () => void;
  demoError?: string | null;
  onDemoRetry?: () => void;
  demoCalendarLink?: string;
  onSendProspectPrefill?: (data: Record<string, string>) => void;
  // End-of-demo wrap-up (populated by bot's `demo_ended` RTVI message)
  demoEnded?: boolean;
  demoSummary?: string | null;
  demoFeaturesShown?: string[];
  demoNextStep?: string | null;
  // Customer brand for "demoing <Product>" chrome
  siteName?: string;
  siteDomain?: string;
  siteLogoUrl?: string;
  // FLO-585: shared launcher corner + inset (init-time config).
  launcherPosition?: 'bottom-right' | 'bottom-left';
  launcherInset?: number;
  // FLO-296: self-hosted favicon (fallback below siteLogoUrl in DemoView)
  siteFaviconUrl?: string;
  // Demo overlay branding (FLO-64) — accent palette + primary CTA copy
  accentColor?: string;
  ctaLabel?: string;
  // Bot audio output mute — controlled by the SDK
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  // Pre-supplied prospect email from the wrapper page
  prefilledEmail?: string;
  // Pre-supplied name/company/role from the wrapper page (a /d/[token] demo
  // link minted with prospect context). Seeds the intro form so the recipient
  // never retypes what the link already knew.
  prefilledProspect?: DemoIntroPrefill;
  // Fires when the intro form's Stage 1 advances (email submitted).
  // The SDK uses this to trigger the deferred pipecat session start.
  onProspectEmailCaptured?: (email: string) => void;
  // Asks floe-api whether a typed email is an obvious fake (see email-check.ts).
  checkProspectEmail?: EmailCheck;
  // The same cost gate with no email — the Start tap under a deferred
  // identity-capture policy, where the ask hasn't happened yet.
  onStartDemoWithoutEmail?: () => void;
  // Resolved identity-capture policy (site ← per-link). Decides whether the
  // intro gate is a form or a fieldless Start card, and which fields the
  // conversational flow asks for. Undefined → today's form.
  demoCapture?: DemoCapturePolicy;
  identityAsk?: IdentityAskState | null;
  onIdentityConfirm?: (value: string) => boolean | void;
  onIdentityReject?: () => void;
  onRequestIdentityAskNow?: (timing: string) => void;
}

export const AgentUI: React.FC<AgentUIProps> = ({ 
  connected, 
  voiceEnabled, 
  agentState, 
  botTranscript = '',
  userTranscript = '',
  screenShareEnabled = false,
  userIsSpeaking = false,
  botIsSpeaking = false,
  audioLevels = [],
  onToggleConnection,
  onToggleMute,
  micPermissionDenied = false,
  micUnavailable = false,
  onToggleScreenShare,
  transcriptHistory = [],
  onResume,
  pausedSessionId: _pausedSessionId,
  connectionProgress,
  fatalError,
  isReturningUser = false,
  supportMode = false,
  onMinimize,
  onExpand,
  onEndSession,
  onSendText,
  suggestedOptions,
  onSelectOption,
  // Nudge props
  nudgeText,
  showNudge,
  onNudgeDismiss,
  nudgeAutoHideDelay,
  nudgeAutoShow,
  // Plan progress props
  planProgress,
  languageOptions = [],
  selectedLanguage = 'en',
  pendingLanguageSwitch,
  languageSwitchError,
  languageSwitchDisabled = false,
  onLanguageChange,
  // Demo mode props
  demoMode = false,
  demoAvailable = false,
  // Absent => an older floe-api, not an unentitled site. See the SDK field.
  websiteAgentEnabled = true,
  websiteAgent = false,
  onStartWebsiteAgent,
  onRequestMicAfterWelcome,
  onOpenWebsiteAgentPreview,
  onRequestWebsiteAgentDemo,
  onRequestWebsiteAgentIdentityPrompt,
  onEndWebsiteAgent,
  websiteAgentOpenerChips,
  websiteAgentNudges,
  websiteAgentAvatarEnabled,
  websiteAgentAvatarIdleLoopUrl,
  websiteAgentAvatarPosterUrl,
  websiteAgentAvatarIntro,
  websiteAgentPushToDemo,
  websiteAgentCtaLabel,
  websiteAgentCalendarRequestId,
  websiteAgentDemoStartingId,
  websiteAgentDemoVisibleId,
  websiteAgentCapture,
  onWebsiteAgentNudgeEvent,
  onWebsiteAgentNavigated,
  onWidgetOpened,
  onWidgetClosed,
  onBookingCtaClick,
  onResolveBookingIntent,
  onBookingUxHint,
  websiteAgentDisplayMode,
  websiteAgentSidebarWidth,
  websiteAgentResumedMessages,
  websiteAgentResumeOutcome,
  embedMode = 'docked',
  demoIntroFormSubmitted = false,
  onMarkIntroFormSubmitted,
  botVideoStream,
  avatarVideoStream,
  onEndDemo,
  onCloseDemo,
  demoError,
  onDemoRetry,
  demoCalendarLink,
  onSendProspectPrefill,
  demoEnded = false,
  demoSummary,
  demoFeaturesShown,
  demoNextStep,
  siteName,
  siteDomain,
  siteLogoUrl,
  siteFaviconUrl,
  accentColor,
  ctaLabel,
  speakerMuted,
  onToggleSpeaker,
  prefilledEmail,
  prefilledProspect,
  onProspectEmailCaptured,
  checkProspectEmail,
  onStartDemoWithoutEmail,
  demoCapture,
  identityAsk, onIdentityConfirm, onIdentityReject, onRequestIdentityAskNow,
  launcherPosition,
  launcherInset,
  avatarIdleAfterMs,
}) => {
  // Every mode's avatar media comes from the SAME site-level clip: floe-api
  // resolves `websiteAgent.avatar` for AgentType.WEBSITE and serves it from
  // validate-client-key with no mode gate, so the demo and onboarding surfaces
  // can use it too. There is no per-agent idle asset today — uploads are
  // website-only server-side.
  //
  // Gated on this session actually having an avatar track. Without that gate a
  // site that uploaded a clip and then turned the video avatar OFF would still
  // get a presenter on the demo/onboarding surfaces — the exact media leak
  // FLO-753 closed for the website launcher. `avatarVideoStream` is the raw
  // prop and does NOT go null during an idle swap (only the derived
  // `liveAvatarStream` below does), so this stays true across the hand-over
  // and false for a session that never had an avatar at all.
  // Latched for the session and reset at its boundary — see
  // `useAvatarSurfaceActive` for why both halves are load-bearing.
  const avatarSurfaceActive = useAvatarSurfaceActive(connected, !!avatarVideoStream);
  const avatarIdleLoopUrl = avatarSurfaceActive ? websiteAgentAvatarIdleLoopUrl : undefined;
  const avatarPosterUrl = avatarSurfaceActive ? websiteAgentAvatarPosterUrl : undefined;

  // MUST run before the demoMode branch below returns — hooks cannot sit
  // behind a conditional return.
  const avatarIdle = useAvatarIdle({
    hasIdleMedia: !!(avatarIdleLoopUrl || avatarPosterUrl),
    hasStream: !!avatarVideoStream,
    botIsSpeaking,
    idleAfterMs: resolveAvatarIdleAfterMs(avatarIdleAfterMs),
  });
  // Dropping the track here is what makes the surfaces fall back to the loop:
  // `avatarHeroSource` already resolves stream -> loop -> poster, and
  // AvatarVideo cross-fades the handover in both directions.
  const liveAvatarStream = avatarIdle ? null : avatarVideoStream;

  // Demo mode: render DemoView instead of the standard onboarding UI
  if (demoMode) {
    return React.createElement(DemoView, {
      // Always true here (this branch only runs in demo mode), but forwarded
      // explicitly so DemoView can key its demo-specific defaults — e.g.
      // starting with the chat sidebar collapsed — off a real prop.
      demoMode: demoMode,
      embedMode: embedMode,
      videoStream: botVideoStream || null,
      avatarStream: liveAvatarStream || null,
      avatarIdleLoopUrl,
      avatarPosterUrl,
      botTranscript: botTranscript,
      botIsSpeaking: botIsSpeaking,
      micEnabled: voiceEnabled,
      onToggleMute: onToggleMute || (() => {}),
      onEndDemo: onEndDemo || (() => {}),
      onCloseDemo: onCloseDemo || (() => {}),
      connected: connected,
      agentState: agentState,
      messages: transcriptHistory,
      currentTranscript: botIsSpeaking ? botTranscript : userTranscript,
      currentRole: botIsSpeaking ? 'assistant' as const : (userTranscript ? 'user' as const : undefined),
      onSendText: onSendText,
      // FLO-307: opener chips + tap handler, rendered above DemoView's input.
      suggestedOptions: suggestedOptions,
      onSelectOption: onSelectOption,
      showIntroForm: demoMode && !demoIntroFormSubmitted,
      onIntroFormSubmit: (data: Record<string, string>) => {
        // Send structured prospect_prefill RTVI message (updates profile + tells Shepherd to skip discovery)
        if (onSendProspectPrefill) {
          onSendProspectPrefill(data);
        } else if (onSendText) {
          // Fallback: send as natural language text. Clause-by-clause from the
          // string pack (FLO-531) — company/role are optional, so a one-shot
          // template would leave dangling fragments in any language. The
          // messageKind marker is what actually triggers pipecat's kickoff;
          // the sentence only has to read naturally to the prospect.
          const parts = [];
          if (data.name) parts.push(getString('demo.kickoff.name', { name: data.name }));
          if (data.phone) parts.push(getString('demo.kickoff.phone', { phone: data.phone }));
          if (data.email) parts.push(getString('demo.kickoff.email', { email: data.email }));
          if (data.company) parts.push(getString('demo.kickoff.company', { company: data.company }));
          if (data.role) parts.push(getString('demo.kickoff.role', { role: data.role }));
          onSendText(parts.join(', ') + getString('demo.kickoff.end'), {
            messageKind: 'intro_form',
          });
        }
        onMarkIntroFormSubmitted?.();
      },
      error: demoError,
      onRetry: onDemoRetry,
      calendarLink: demoCalendarLink,
      // CDP Phase 1 (FLO-547): BOOKING_CTA_CLICKED — fired from the
      // Cal/Calendly CTAs DemoView renders (error state, wrap-up, post-demo).
      onBookingCtaClick,
      onResolveBookingIntent,
      onBookingUxHint,
      demoEnded,
      demoSummary,
      featuresShown: demoFeaturesShown,
      nextStep: demoNextStep,
      siteName,
      siteDomain,
      siteLogoUrl,
      siteFaviconUrl,
      accentColor,
      ctaLabel,
      speakerMuted,
      onToggleSpeaker,
      prefilledEmail,
      prefilledProspect,
      onProspectEmailCaptured,
      checkProspectEmail,
      onStartDemoWithoutEmail,
      demoCapture,
      identityAsk, onIdentityConfirm, onIdentityReject,
      languageOptions,
      selectedLanguage,
      pendingLanguageSwitch,
      languageSwitchError,
      languageSwitchDisabled,
      onLanguageChange,
    });
  }

  // FLO-320: website-agent mode — a self-contained launcher pill + chat window
  // (WebsiteAgentChat). Rendered as a dedicated branch (before the onboarding
  // hooks below) so the demo and onboarding paths are byte-for-byte unchanged.
  // WebsiteAgentView owns its own open/closed state and hooks.
  if (websiteAgent) {
    return React.createElement(WebsiteAgentView, {
      connected,
      agentState,
      voiceEnabled,
      botTranscript,
      userTranscript,
      transcriptHistory,
      userIsSpeaking,
      botIsSpeaking,
      audioLevels,
      connectionProgress,
      fatalError,
      onToggleMute,
      micPermissionDenied,
      micUnavailable,
      onSendText,
      speakerMuted,
      onToggleSpeaker,
      onEndSession: onEndWebsiteAgent,
      // FLO-323: the live-demo screencast (bot video track). When present, the
      // website-agent opens an inline demo panel beside the chat (not a takeover).
      botVideoStream,
      onStartWebsiteAgent: onStartWebsiteAgent || (() => {}),
      // FLO-786: only the voice launchers arm this — WebsiteAgentView decides.
      onRequestMicAfterWelcome,
      onOpenWebsiteAgentPreview,
      onRequestLiveDemo: onRequestWebsiteAgentDemo,
      onRequestDemoIdentityPrompt: onRequestWebsiteAgentIdentityPrompt,
      siteName,
      siteDomain,
      siteLogoUrl,
      siteFaviconUrl,
      accentColor,
      // FLO-318/349: resolved website-agent config.
      openerChips: websiteAgentOpenerChips,
      // FLO-361: server-pushed, conversation-aware follow-up suggestions (the
      // same `suggested_options` channel DemoView uses). Rendered as the
      // follow-up chips after each answer, replacing the old static leftovers.
      // Tapping one routes through onSendText, which clears suggestedOptions —
      // so no separate onSelectOption is needed here.
      suggestedOptions,
      nudges: websiteAgentNudges,
      // The "see it live" CTA needs BOTH a runnable demo AND the operator's
      // push-to-demo facet left on.
      demoAvailable: demoAvailable && websiteAgentPushToDemo !== false,
      websiteAgentEnabled,
      ctaLabel: websiteAgentCtaLabel,
      // FLO-618: booking. `demoCalendarLink` is a legacy NAME, not a legacy
      // scope — the SDK resolves it once from `config.demoCalendarLink ||
      // Site.calBookingUrl`; both agents receive the same HTTPS destination and
      // read that one value. Its presence is the whole gate: no configured URL,
      // no booking controls anywhere on the surface.
      calendarLink: demoCalendarLink,
      onBookingCtaClick,
      onResolveBookingIntent,
      onBookingUxHint,
      calendarRequestId: websiteAgentCalendarRequestId,
      // The hand-over edges. Only this branch receives them — DemoView's stage
      // is never in a website→demo transition, and the onboarding branch has no
      // stage to cover.
      demoStartingId: websiteAgentDemoStartingId,
      demoVisibleId: websiteAgentDemoVisibleId,
      avatarEnabled: websiteAgentAvatarEnabled,
      avatarVideoStream: liveAvatarStream,
      // FLO-520: enabled idle-loop media → launcher tile + panel hero.
      avatarIdleLoopUrl: websiteAgentAvatarIdleLoopUrl,
      avatarPosterUrl: websiteAgentAvatarPosterUrl,
      avatarIntro: websiteAgentAvatarIntro,
      capture: websiteAgentCapture,
      identityAsk, onIdentityConfirm, onIdentityReject, onRequestIdentityAskNow,
      onNudgeEvent: onWebsiteAgentNudgeEvent,
      onNavigated: onWebsiteAgentNavigated,
      // CDP Phase 1 (FLO-547): WIDGET_OPENED/WIDGET_CLOSED on real panel
      // transitions (the FLO-524 restore paints open without firing these).
      onWidgetOpened,
      onWidgetClosed,
      // FLO-514/519: per-site display mode. Absent → WebsiteAgentView's
      // 'floating' default, i.e. the corner-anchored Q&A card.
      displayMode: websiteAgentDisplayMode,
      sidebarWidth: websiteAgentSidebarWidth,
      launcherPosition,
      launcherInset,
      // FLO-544: the server's version of the conversation, when it resumed one.
      resumedMessages: websiteAgentResumedMessages,
      // Resume honesty: 'fresh' + a restored transcript = the divider.
      resumeOutcome: websiteAgentResumeOutcome,
      languageOptions,
      selectedLanguage,
      pendingLanguageSwitch,
      languageSwitchError,
      languageSwitchDisabled,
      onLanguageChange,
    });
  }

  // FLO-51: the onboarding agent now renders the SAME chat surface as the
  // website agent (WebsiteAgentChat, via OnboardingView) — one set of controls,
  // one set of button positions, one set of behaviours — docked rather than
  // modal, because this agent drives the host page and must not cover it.
  //
  // OnboardingView owns its own open/closed state, so the panel/orb bookkeeping
  // that used to live here is gone. `onToggleConnection` is no longer a UI
  // control: starting is the launcher click (onStartSession) and ending is the
  // header's End, which are now visibly different actions rather than two
  // identical-looking header buttons.
  useEffect(() => {
    logger.log('[AgentUI] Rendering onboarding surface:', {
      connected,
      agentState,
      voiceEnabled,
      speakerMuted,
      screenShareEnabled,
      historyCount: transcriptHistory.length,
    });
  }, [connected, agentState, voiceEnabled, speakerMuted, screenShareEnabled, transcriptHistory.length]);

  return (
    <OnboardingView
      connected={connected}
      agentState={agentState}
      voiceEnabled={voiceEnabled}
      botTranscript={botTranscript}
      userTranscript={userTranscript}
      transcriptHistory={transcriptHistory}
      connectionProgress={connectionProgress}
      fatalError={fatalError}
      onToggleMute={onToggleMute}
      micPermissionDenied={micPermissionDenied}
      micUnavailable={micUnavailable}
      speakerMuted={speakerMuted}
      onToggleSpeaker={onToggleSpeaker}
      onSendText={onSendText}
      screenShareEnabled={screenShareEnabled}
      onToggleScreenShare={onToggleScreenShare}
      onStartSession={onToggleConnection || (() => {})}
      onEndSession={onEndSession}
      onResume={onResume}
      onMinimize={onMinimize}
      onExpand={onExpand}
      planProgress={planProgress}
      suggestedOptions={suggestedOptions}
      isReturningUser={isReturningUser}
      supportMode={supportMode}
      nudgeText={nudgeText}
      showNudge={showNudge}
      onNudgeDismiss={onNudgeDismiss}
      nudgeAutoHideDelay={nudgeAutoHideDelay}
      nudgeAutoShow={nudgeAutoShow}
      siteName={siteName}
      siteLogoUrl={siteLogoUrl}
      siteFaviconUrl={siteFaviconUrl}
      accentColor={accentColor}
      launcherPosition={launcherPosition}
      launcherInset={launcherInset}
      // FLO-545: onboarding-surface avatar — the live face renders in the
      // chat hero when the avatar's track is up. `avatarEnabled` used to be
      // derived from the stream's presence, which made the whole face surface
      // vanish the moment the track dropped; it now tracks whether this
      // session ever had an avatar, so an idle swap falls back to the loop
      // instead of collapsing the layout.
      avatarEnabled={avatarSurfaceActive}
      avatarIdleLoopUrl={avatarIdleLoopUrl}
      avatarPosterUrl={avatarPosterUrl}
      avatarVideoStream={liveAvatarStream || null}
    />
  );
};
